import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';

import { accountabilityAPI } from '@/services/accountabilityAPI';
import { AccountabilityStats } from '@/types/accountability';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import { TextField } from '@mui/material';

import Alert from '@/components/Alert';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import styled from '@/styles';

const TabBar = styled('div', {
  display: 'inline-flex',
  backgroundColor: 'white',
  borderRadius: '$lg $lg 0 0',
  padding: '0 4px',
  borderBottom: '1px solid $colors$neutralLightest',
});

const TabLabel = styled('span', {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$colors$textDefault',
  borderBottom: '2px solid $colors$themeDefault',
});

const StatCard = styled('div', {
  padding: '$md',
  borderRadius: '$lg',
  border: '1px solid $colors$neutralA4',
  textAlign: 'center',
  flex: 1,
});

const StatValue = styled('div', {
  fontSize: '2rem',
  fontWeight: '700',
  lineHeight: 1.2,
  marginBottom: '4px',
});

const StatLabel = styled('div', {
  fontSize: '0.875rem',
  color: '$colors$textLight',
});

const StatBreakdown = styled('div', {
  fontSize: '0.75rem',
  color: '$colors$textLight',
  marginTop: '4px',
});

const SectionTitle = styled('div', {
  fontSize: '1rem',
  fontWeight: '600',
  marginBottom: '12px',
});

const StatRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: '1px solid $colors$neutralA2',
  '&:last-child': {
    borderBottom: 'none',
  },
});

export const StatsPage: React.FC = () => {
  const [stats, setStats] = useState<AccountabilityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 30), 'yyyy-MM-dd'),
  );
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getStats(startDate, endDate);

      if (response.success) {
        setStats(response.stats);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load statistics';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <Page size="full" title="Tasks">
        <TabBar>
          <TabLabel>Violations</TabLabel>
        </TabBar>
        <Card>
          <Card.Section>
            <LoadingState overlayColor="white" />
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page size="full" title="Tasks">
      <Tabs selected={0} tabs={VIOLATIONS_TAB} onSelect={() => {}} />

      {error && (
        <Card>
          <Card.Section>
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="Error"
            />
          </Card.Section>
        </Card>
      )}

      <Card>
        <Card.Section>
          <Stack gap={16} style={{ flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              sx={{ minWidth: 160 }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              sx={{ minWidth: 160 }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Card.Section>
      </Card>

      {stats && (
        <Card>
          <Card.Section>
            <Stack gap={16}>
              <StatCard>
                <StatValue>{stats.violations.total}</StatValue>
                <StatLabel>Total Violations</StatLabel>
                <StatBreakdown>
                  {stats.violations.late_drops} late drops &middot;{' '}
                  {stats.violations.no_shows} no shows
                </StatBreakdown>
              </StatCard>
              <StatCard>
                <StatValue>{stats.suspensions.active}</StatValue>
                <StatLabel>Active Suspensions</StatLabel>
                <StatBreakdown>{stats.suspensions.total} total</StatBreakdown>
              </StatCard>
              <StatCard>
                <StatValue>{stats.appeals.pending}</StatValue>
                <StatLabel>Pending Appeals</StatLabel>
                <StatBreakdown>{stats.appeals.total} total</StatBreakdown>
              </StatCard>
              <StatCard>
                <StatValue>{stats.workers.workers_with_violations}</StatValue>
                <StatLabel>Workers with Violations</StatLabel>
                <StatBreakdown>
                  {stats.workers.repeat_offenders} repeat offenders
                </StatBreakdown>
              </StatCard>
            </Stack>
          </Card.Section>

          <Card.Section>
            <Stack gap={24}>
              <div style={{ flex: 1 }}>
                <SectionTitle>Violations by Status</SectionTitle>
                <StatRow>
                  <Body>Active</Body>
                  <Body weight="medium">{stats.violations.active}</Body>
                </StatRow>
                <StatRow>
                  <Body>Forgiven</Body>
                  <Body weight="medium">{stats.violations.forgiven}</Body>
                </StatRow>
                <StatRow>
                  <Body>Removed</Body>
                  <Body weight="medium">{stats.violations.removed}</Body>
                </StatRow>
              </div>

              <div style={{ flex: 1 }}>
                <SectionTitle>Suspensions by Status</SectionTitle>
                <StatRow>
                  <Body>Active</Body>
                  <Body weight="medium">{stats.suspensions.active}</Body>
                </StatRow>
                <StatRow>
                  <Body>Completed</Body>
                  <Body weight="medium">{stats.suspensions.completed}</Body>
                </StatRow>
                <StatRow>
                  <Body>Lifted</Body>
                  <Body weight="medium">{stats.suspensions.lifted}</Body>
                </StatRow>
                <StatRow>
                  <Body>Permanent</Body>
                  <Body weight="medium">{stats.suspensions.permanent}</Body>
                </StatRow>
              </div>

              <div style={{ flex: 1 }}>
                <SectionTitle>Appeals</SectionTitle>
                <StatRow>
                  <Body>Pending</Body>
                  <Body weight="medium">{stats.appeals.pending}</Body>
                </StatRow>
                <StatRow>
                  <Body>Approved</Body>
                  <Body weight="medium">{stats.appeals.approved}</Body>
                </StatRow>
                <StatRow>
                  <Body>Denied</Body>
                  <Body weight="medium">{stats.appeals.denied}</Body>
                </StatRow>
                <StatRow>
                  <Body>Avg Resolution</Body>
                  <Body weight="medium">
                    {stats.appeals.average_resolution_time
                      ? `${Math.round(stats.appeals.average_resolution_time)} hrs`
                      : '-'}
                  </Body>
                </StatRow>
              </div>
            </Stack>
          </Card.Section>

          <Card.Section>
            <SectionTitle>Worker Impact</SectionTitle>
            <Stack gap={16}>
              <StatCard>
                <StatValue>{stats.workers.total_suspended}</StatValue>
                <StatLabel>Currently Suspended</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.workers.total_permanently_removed}</StatValue>
                <StatLabel>Permanently Removed</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.workers.repeat_offenders}</StatValue>
                <StatLabel>Repeat Offenders</StatLabel>
              </StatCard>
            </Stack>
          </Card.Section>
        </Card>
      )}
    </Page>
  );
};

export default StatsPage;
